const TERMINAL_EVENT_TYPES = new Set(["done", "error", "terminated"]);
/** A ready piece of the answer (table, SQL, chart, insights), sent before the turn completes. */
export const ANSWER_PART_EVENT = "answer_part";
/** The answer validator approved the answer: every part up to `revision` is final. */
export const ANSWER_VALIDATED_EVENT = "answer_validated";
const NON_VERBOSE_EVENT_TYPES = new Set([
  ANSWER_PART_EVENT,
  ANSWER_VALIDATED_EVENT,
  "token",
  "done",
  "error",
  "terminated",
  "follow_up_questions",
  "session_title",
  "ping",
  "clarification_required",
  "waiting_for_clarification",
]);

export type LlmCallMessage = {
  role: string;
  content: string;
};

export type TurnStreamEvent = {
  type: string;
  content?: string;
  label?: string;
  message?: string;
  delta?: string;
  step_title?: string;
  summary?: string;
  insights?: string[];
  why?: string;
  why_points?: string[];
  action?: string;
  action_points?: string[];
  business_impact?: string;
  sources?: unknown;
  /** Raw `done` payload field; parsed by `parseLearningHistoryResolutions`. */
  learning_history_resolutions?: unknown;
  detail?: string;
  questions?: string[];
  request?: Record<string, unknown>;
  status?: string;
  turn_id?: string;
  messages?: LlmCallMessage[];
  raw_response?: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  sql?: string;
  /** `answer_part` only: which part this is (`table`, `queries`, `chart`, `insights`). */
  part?: string;
  /** `answer_part` / `answer_validated`: the SQL run the data belongs to. */
  revision?: number;
  /** `answer_part`: sent before the answer validator approved it. */
  provisional?: boolean;
  /** `answer_part`: the part's data, in the same shape the finished turn carries. */
  answer_part?: unknown;
  duration_ms?: number;
  /** Where the step ended on the turn's timeline, in ms from the turn's start. */
  elapsed_ms?: number;
};

export type PipelineVerboseStep = {
  stepIndex: number;
  eventType: string;
  stepTitle: string;
  summary: string;
  detail: string;
  messages?: LlmCallMessage[];
  rawResponse?: string;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  /** Untrimmed statement from the step payload; the detail body may carry a trimmed copy. */
  sql?: string;
  /** How long this step took, when the backend measured it. */
  durationMs?: number;
  /**
   * When the step ended, in ms from the turn's start. With `durationMs` it places the step on
   * the turn's timeline, which is how the time breakdown shows steps that ran side by side.
   */
  elapsedMs?: number;
  /** When the step was stored (ms since the epoch); history only, it marks clarification waits. */
  createdAt?: number;
};

export type ParsedTurnStreamEvent = TurnStreamEvent & {
  verboseStep: PipelineVerboseStep | null;
};

/**
 * Drops the inline `SQL:` section from a verbose step's detail body.
 *
 * The backend writes a generation attempt's detail as `Purpose: …`, a `SQL:` section holding
 * the statement, then optional `Error:` / `Validation trace:` / `Took N ms` sections, each
 * separated by a blank line. A step that also carries the statement in its payload renders it
 * from there instead, so the inline copy is dropped — turns generated before the trim was
 * removed stored it cut off at 500 characters.
 */
export function detailWithoutSqlSection(detail: string): string {
  const sectionStart = detail.search(/^SQL:$/m);
  if (sectionStart < 0) {
    return detail;
  }
  const sectionEnd = detail.indexOf("\n\n", sectionStart);
  const before = detail.slice(0, sectionStart);
  const after = sectionEnd < 0 ? "" : detail.slice(sectionEnd + 2);
  return `${before}${after}`.trim();
}

export function parseVerboseStep(
  event: TurnStreamEvent,
  stepIndex: number,
): PipelineVerboseStep | null {
  if (NON_VERBOSE_EVENT_TYPES.has(event.type)) {
    return null;
  }

  const stepTitle = event.step_title?.trim();
  const summary = event.summary?.trim();
  const detail = event.detail?.trim();
  const legacyContent = event.content ?? event.label ?? event.message;

  // `detail` is optional: with APP_CHAT_VERBOSE_HEADINGS_ONLY on, the backend sends the
  // headings and withholds the step body, and the step must still show up in the log.
  if (stepTitle && summary) {
    return {
      stepIndex,
      eventType: event.type,
      stepTitle,
      summary,
      detail: detail ?? "",
      messages: event.messages,
      rawResponse: event.raw_response,
      input: event.input,
      output: event.output,
      sql: event.sql,
      durationMs: event.duration_ms,
      elapsedMs: event.elapsed_ms,
    };
  }

  if (typeof legacyContent === "string" && legacyContent.trim().length > 0) {
    const text = legacyContent.trim();
    return {
      stepIndex,
      eventType: event.type,
      stepTitle: text,
      summary: text,
      detail: text,
    };
  }

  return null;
}

export function parseTurnStreamEvent(
  rawData: string,
  eventName?: string,
  stepIndex = 0,
): ParsedTurnStreamEvent | null {
  const trimmed = rawData.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const payload = JSON.parse(trimmed) as Record<string, unknown>;
    const type =
      typeof payload.type === "string"
        ? payload.type
        : typeof eventName === "string" && eventName.length > 0
          ? eventName
          : "message";

    const event: TurnStreamEvent = {
      type,
      content: typeof payload.content === "string" ? payload.content : undefined,
      label: typeof payload.label === "string" ? payload.label : undefined,
      message: typeof payload.message === "string" ? payload.message : undefined,
      delta: typeof payload.delta === "string" ? payload.delta : undefined,
      step_title: typeof payload.step_title === "string" ? payload.step_title : undefined,
      summary: typeof payload.summary === "string" ? payload.summary : undefined,
      insights: Array.isArray(payload.insights)
        ? payload.insights.filter((item): item is string => typeof item === "string")
        : undefined,
      why: typeof payload.why === "string" ? payload.why : undefined,
      why_points: Array.isArray(payload.why_points)
        ? payload.why_points.filter((item): item is string => typeof item === "string")
        : undefined,
      action: typeof payload.action === "string" ? payload.action : undefined,
      action_points: Array.isArray(payload.action_points)
        ? payload.action_points.filter((item): item is string => typeof item === "string")
        : undefined,
      business_impact: typeof payload.business_impact === "string" ? payload.business_impact : undefined,
      sources: Array.isArray(payload.sources) ? payload.sources : undefined,
      learning_history_resolutions: Array.isArray(payload.learning_history_resolutions)
        ? payload.learning_history_resolutions
        : undefined,
      detail: typeof payload.detail === "string" ? payload.detail : undefined,
      questions: Array.isArray(payload.questions)
        ? payload.questions.filter((q): q is string => typeof q === "string")
        : undefined,
      request:
        payload.request && typeof payload.request === "object"
          ? (payload.request as Record<string, unknown>)
          : undefined,
      status: typeof payload.status === "string" ? payload.status : undefined,
      turn_id: typeof payload.turn_id === "string" ? payload.turn_id : undefined,
      messages: Array.isArray(payload.messages)
        ? payload.messages.filter(
            (item): item is LlmCallMessage =>
              typeof item === "object" &&
              item !== null &&
              typeof (item as LlmCallMessage).role === "string" &&
              typeof (item as LlmCallMessage).content === "string",
          )
        : undefined,
      raw_response: typeof payload.raw_response === "string" ? payload.raw_response : undefined,
      input:
        payload.input && typeof payload.input === "object"
          ? (payload.input as Record<string, unknown>)
          : undefined,
      output:
        payload.output && typeof payload.output === "object"
          ? (payload.output as Record<string, unknown>)
          : undefined,
      sql: typeof payload.sql === "string" ? payload.sql : undefined,
      part: typeof payload.part === "string" ? payload.part : undefined,
      revision: typeof payload.revision === "number" ? payload.revision : undefined,
      provisional: typeof payload.provisional === "boolean" ? payload.provisional : undefined,
      answer_part: payload.answer_part,
      duration_ms: typeof payload.duration_ms === "number" ? payload.duration_ms : undefined,
      elapsed_ms: typeof payload.elapsed_ms === "number" ? payload.elapsed_ms : undefined,
    };

    return {
      ...event,
      verboseStep: parseVerboseStep(event, stepIndex),
    };
  } catch {
    if (typeof eventName === "string" && TERMINAL_EVENT_TYPES.has(eventName)) {
      return {
        type: eventName,
        verboseStep: null,
      };
    }

    return {
      type: eventName ?? "message",
      content: trimmed,
      verboseStep: null,
    };
  }
}

export function parseSseChunk(chunk: string): {
  events: ParsedTurnStreamEvent[];
  remainder: string;
} {
  const events: ParsedTurnStreamEvent[] = [];
  const normalized = chunk.replace(/\r\n/g, "\n");
  const blocks = normalized.split("\n\n");
  const remainder = blocks.pop() ?? "";

  for (const block of blocks) {
    if (!block.trim()) {
      continue;
    }

    let eventName: string | undefined;
    let stepIndex = 0;
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) {
        eventName = line.slice("event:".length).trim();
        continue;
      }
      if (line.startsWith("id:")) {
        const parsed = Number.parseInt(line.slice("id:".length).trim(), 10);
        if (!Number.isNaN(parsed)) {
          stepIndex = parsed;
        }
        continue;
      }
      if (line.startsWith("data:")) {
        dataLines.push(line.slice("data:".length).trimStart());
      }
    }

    if (dataLines.length === 0) {
      continue;
    }

    const parsed = parseTurnStreamEvent(dataLines.join("\n"), eventName, stepIndex);
    if (parsed) {
      events.push(parsed);
    }
  }

  return { events, remainder };
}

export function shouldUseTabbedResponse(args: {
  text: string;
  verboseLog: PipelineVerboseStep[];
  followUpQuestions: string[];
}): boolean {
  const trimmed = args.text.trim();
  const isSingleLine = !trimmed.includes("\n");
  const isShort = trimmed.length <= 120;
  const hasExtras = args.verboseLog.length > 0 || args.followUpQuestions.length > 0;

  if (isSingleLine && isShort && !hasExtras) {
    return false;
  }

  return true;
}
